import { addMonths, isAfter, isValid, parseISO } from "date-fns";
import { localStorageService } from "@entrilia/shared/services/storage";

export const DRILLDOWN_LEDGER_COLUMNS_STORAGE_KEY = "drilldown_ledger_columns_setup_v1";

const EXPIRATION_MONTHS = 3;

export type LedgerBucket = "ledger" | "allocated" | "apar" | "vat";

export interface DrillDownLedgerColumnsScope {
  reportId: string;
  userId: string;
  ledgerBucket: LedgerBucket;
}

export interface PersistedDrillDownSorting {
  dimensionName: string;
  sortAsc: boolean;
  order: number;
}

interface PersistedDrillDownLedgerColumnsEntry {
  selectedColumnNames: string[];
  sorting: PersistedDrillDownSorting[];
  expiresAtIso: string;
}

type PersistedDrillDownLedgerColumnsState = Record<string, PersistedDrillDownLedgerColumnsEntry>;

export interface DrillDownLedgerColumnsSetup {
  selectedColumnNames: string[];
  sorting: PersistedDrillDownSorting[];
}

const safeRemoveStorageState = (): void => {
  try {
    localStorageService.removeItem(DRILLDOWN_LEDGER_COLUMNS_STORAGE_KEY);
  } catch {
    // Intentionally ignored to avoid breaking drilldown flow.
  }
};

const sortByOrder = <T extends { order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.order - b.order).map((item, index) => ({ ...item, order: index }));

const getScopeKey = ({ reportId, userId, ledgerBucket }: DrillDownLedgerColumnsScope): string => {
  return `${reportId}|${userId.toLowerCase()}|${ledgerBucket}`;
};

const getStorageState = (): PersistedDrillDownLedgerColumnsState => {
  try {
    return localStorageService.getItemWithFallback<PersistedDrillDownLedgerColumnsState>(
      DRILLDOWN_LEDGER_COLUMNS_STORAGE_KEY,
      {}
    );
  } catch {
    safeRemoveStorageState();
    return {};
  }
};

const saveStorageState = (state: PersistedDrillDownLedgerColumnsState): void => {
  if (Object.keys(state).length === 0) {
    safeRemoveStorageState();
    return;
  }

  const stateCopy: PersistedDrillDownLedgerColumnsState = { ...state };

  while (Object.keys(stateCopy).length > 0) {
    try {
      localStorageService.setItem(DRILLDOWN_LEDGER_COLUMNS_STORAGE_KEY, stateCopy);
      return;
    } catch {
      const oldestEntryKey = getOldestEntryKey(stateCopy);
      if (oldestEntryKey === undefined) {
        break;
      }
      delete stateCopy[oldestEntryKey];
    }
  }

  safeRemoveStorageState();
};

const getOldestEntryKey = (state: PersistedDrillDownLedgerColumnsState): string | undefined => {
  const sortedEntries = Object.entries(state).sort(([, firstEntry], [, secondEntry]) => {
    const firstExpiresAt = parseISO(firstEntry.expiresAtIso);
    const secondExpiresAt = parseISO(secondEntry.expiresAtIso);

    const firstValue = isValid(firstExpiresAt) ? firstExpiresAt.getTime() : Number.NEGATIVE_INFINITY;
    const secondValue = isValid(secondExpiresAt) ? secondExpiresAt.getTime() : Number.NEGATIVE_INFINITY;

    return firstValue - secondValue;
  });

  return sortedEntries[0]?.[0];
};

const entryExpired = (entry: unknown, now: Date): boolean => {
  if (typeof entry !== "object" || entry === null) {
    return true;
  }

  const expiresAtIso = (entry as { expiresAtIso?: unknown }).expiresAtIso;
  if (typeof expiresAtIso !== "string") {
    return true;
  }

  const expiresAt = parseISO(expiresAtIso);
  return !isValid(expiresAt) || isAfter(now, expiresAt);
};

const pruneExpiredEntries = (
  state: PersistedDrillDownLedgerColumnsState,
  now: Date
): { cleanedState: PersistedDrillDownLedgerColumnsState; changed: boolean } => {
  const cleanedState: PersistedDrillDownLedgerColumnsState = {};
  let changed = false;

  Object.entries(state).forEach(([key, value]) => {
    if (entryExpired(value, now)) {
      changed = true;
      return;
    }

    cleanedState[key] = value;
  });

  return { cleanedState, changed };
};

const get = (scope: DrillDownLedgerColumnsScope): DrillDownLedgerColumnsSetup | undefined => {
  try {
    const now = new Date();
    const scopeKey = getScopeKey(scope);
    const state = getStorageState();
    const { cleanedState, changed } = pruneExpiredEntries(state, now);

    const entry = cleanedState[scopeKey];
    if (!entry) {
      if (changed) {
        saveStorageState(cleanedState);
      }
      return undefined;
    }

    const selectedColumnNames = Array.isArray(entry.selectedColumnNames)
      ? entry.selectedColumnNames.filter((column): column is string => typeof column === "string")
      : [];

    const sorting = Array.isArray(entry.sorting)
      ? entry.sorting
          .filter(
            (item): item is PersistedDrillDownSorting =>
              typeof item.dimensionName === "string" &&
              typeof item.sortAsc === "boolean" &&
              typeof item.order === "number"
          )
          .map((item) => ({ ...item }))
      : [];

    if (selectedColumnNames.length === 0) {
      delete cleanedState[scopeKey];
      saveStorageState(cleanedState);
      return undefined;
    }

    cleanedState[scopeKey] = {
      ...entry,
      selectedColumnNames: [...selectedColumnNames],
      expiresAtIso: addMonths(now, EXPIRATION_MONTHS).toISOString(),
      sorting: sortByOrder(sorting),
    };
    saveStorageState(cleanedState);

    return {
      selectedColumnNames: [...selectedColumnNames],
      sorting: sortByOrder(sorting),
    };
  } catch {
    return undefined;
  }
};

const save = (scope: DrillDownLedgerColumnsScope, setup: DrillDownLedgerColumnsSetup): void => {
  try {
    const now = new Date();
    const scopeKey = getScopeKey(scope);
    const state = getStorageState();
    const { cleanedState } = pruneExpiredEntries(state, now);

    cleanedState[scopeKey] = {
      selectedColumnNames: [...setup.selectedColumnNames],
      sorting: sortByOrder(setup.sorting),
      expiresAtIso: addMonths(now, EXPIRATION_MONTHS).toISOString(),
    };

    saveStorageState(cleanedState);
  } catch {
    // Intentionally ignored to avoid breaking drilldown flow.
  }
};

const clear = (scope: DrillDownLedgerColumnsScope): void => {
  try {
    const now = new Date();
    const scopeKey = getScopeKey(scope);
    const state = getStorageState();
    const { cleanedState } = pruneExpiredEntries(state, now);

    delete cleanedState[scopeKey];
    saveStorageState(cleanedState);
  } catch {
    // Intentionally ignored to avoid breaking drilldown flow.
  }
};

export const drillDownLedgerColumnsStorage = {
  get,
  save,
  clear,
};
